import { useTranslation } from 'react-i18next'

interface FieldChange {
  field: string
  oldValue: any
  newValue: any
}

interface ActivityLogEntry {
  title: string
  description: string
}

// User lookup map for formatting assignedTo names
type UserMap = Record<string, string>

export const useSnagFieldChangeDetector = () => {
  const { t } = useTranslation()

  const getFieldDisplayName = (field: string): string => {
    const fieldMap: Record<string, string> = {
      title: t('Snags_activity_field_title', 'Title'),
      description: t('Snags_activity_field_description', 'Description'),
      status: t('Snags_activity_field_status', 'Status'),
      priority: t('Snags_activity_field_priority', 'Priority'),
      dueDate: t('Snags_activity_field_dueDate', 'Due Date'),
      infrastructureType: t('Snags_activity_field_infrastructureType', 'Infrastructure Type'),
      assignedToId: t('Snags_activity_field_assignedTo', 'Assigned To'),
      pinLocation: t('Snags_activity_field_pinLocation', 'Pin Location'),
    }
    return fieldMap[field] || field
  }

  const formatValue = (value: any, field: string, context?: { userMap?: UserMap; oldData?: any; newData?: any }): string => {
    if (value === null || value === undefined || value === '') {
      return t('Snags_activity_value_none', 'None')
    }

    switch (field) {
      case 'dueDate':
        return new Date(value).toLocaleDateString()
      case 'assignedToId':
        // Try to get user name from the userMap
        if (context?.userMap?.[value]) {
          return context.userMap[value]
        }
        // Try to get from oldData/newData assignedTo object
        const data = context?.oldData || context?.newData
        if (data?.assignedTo?.name && data?.assignedTo?.id === value) {
          return data.assignedTo.name
        }
        return value // Fallback to ID if name not available
      case 'status':
        return t(`Snags_status_${value.toLowerCase()}`, value)
      case 'priority':
        return t(`Snags_priority_${value.toLowerCase()}`, value)
      case 'pinLocation':
        return value // Already formatted as "X: ..., Y: ..."
      default:
        return String(value)
    }
  }

  const detectChanges = (oldData: any, newData: any): FieldChange[] => {
    const changes: FieldChange[] = []
    const fieldsToTrack = [
      'title',
      'description',
      'status',
      'priority',
      'dueDate',
      'infrastructureType',
      'assignedToId',
    ]

    fieldsToTrack.forEach((field) => {
      let oldValue = oldData?.[field]
      let newValue = newData?.[field]

      // Special handling for assignedToId - extract ID from assignedTo object if present
      if (field === 'assignedToId') {
        if (oldData?.assignedTo && typeof oldData.assignedTo === 'object') {
          oldValue = oldData.assignedTo.id
        }
      }

      // Handle date comparison - compare as ISO strings
      if (field === 'dueDate') {
        oldValue = oldValue ? new Date(oldValue).toISOString().split('T')[0] : ''
        newValue = newValue ? new Date(newValue).toISOString().split('T')[0] : ''
      }

      // Handle different data types and null/undefined values
      const normalizedOldValue = oldValue === null || oldValue === undefined ? '' : oldValue
      const normalizedNewValue = newValue === null || newValue === undefined ? '' : newValue

      if (String(normalizedOldValue) !== String(normalizedNewValue)) {
        changes.push({
          field,
          oldValue: oldData?.[field] ?? '', // Keep original value for formatting
          newValue: newData?.[field] ?? '',
        })
      }
    })

    // Special handling for pin location (combined pinX and pinY)
    const oldHasPin = oldData?.pinX !== null && oldData?.pinX !== undefined
    const newHasPin = newData?.pinX !== null && newData?.pinX !== undefined
    const pinChanged = oldData?.pinX !== newData?.pinX || oldData?.pinY !== newData?.pinY

    if (pinChanged) {
      changes.push({
        field: 'pinLocation',
        oldValue: oldHasPin ? `X: ${oldData.pinX?.toFixed?.(1) ?? oldData.pinX}%, Y: ${oldData.pinY?.toFixed?.(1) ?? oldData.pinY}%` : '',
        newValue: newHasPin ? `X: ${newData.pinX?.toFixed?.(1) ?? newData.pinX}%, Y: ${newData.pinY?.toFixed?.(1) ?? newData.pinY}%` : '',
      })
    }

    return changes
  }

  const generateActivityLogs = (
    changes: FieldChange[],
    context?: { userMap?: UserMap; oldData?: any; newData?: any },
  ): ActivityLogEntry[] => {
    return changes.map((change) => {
      const fieldName = getFieldDisplayName(change.field)
      const oldValueFormatted = formatValue(change.oldValue, change.field, context)
      const newValueFormatted = formatValue(change.newValue, change.field, context)

      return {
        title: fieldName,
        description: `${oldValueFormatted} → ${newValueFormatted}`,
      }
    })
  }

  return {
    detectChanges,
    generateActivityLogs,
    getFieldDisplayName,
    formatValue,
  }
}
